/**
 * Pedidos que el ERP manda de a muchos seguidos (sincronizar SKU o packs con Mercado Libre): ante un 429 (límite de
 * pedidos, DECISIONES 2026-10-10) esperan y reintentan el mismo pedido, con tope. Lo usan "Empezar a sincronizar estos
 * SKU" de un conteo y "Packs sin sincronizar".
 */

/** 429: reintentos por pedido antes de darlo por fallido, y espera máxima entre intentos. */
export const REINTENTOS_POR_LIMITE = 10;
const ESPERA_MAXIMA_S = 60;

/** Se agotaron los reintentos por 429: el servidor no aplicó nada (rechazó antes del handler). */
export const SIGUE_EL_LIMITE = Symbol('sigue el límite de pedidos');
/** Se cerró la ventana: no se hacen más pedidos. */
export const CANCELADO = Symbol('cancelado');

/** Sin respuesta (red, timeout del proxy) o 5xx: el pedido pudo haberse aplicado igual. */
export const incierto = (e: unknown) => {
  const status = (e as { status?: unknown } | null)?.status;
  return typeof status !== 'number' || status >= 500;
};

/** Segundos a esperar tras el 429 número `intento` (1, 2, …): Retry-After si vino; si no, 10, 20, … hasta 60. */
const esperaPorLimite = (e: unknown, intento: number) => {
  const retryAfter = (e as { retryAfter?: unknown }).retryAfter;
  return Math.min(typeof retryAfter === 'number' && retryAfter > 0 ? retryAfter : 10 * intento, ESPERA_MAXIMA_S);
};

/**
 * Hace `pedir`; ante un 429 avisa que espera, espera y reintenta (hasta REINTENTOS_POR_LIMITE veces: después lanza
 * SIGUE_EL_LIMITE). Antes de cada pedido, también después de cada espera, si `sigue()` es false lanza CANCELADO.
 */
export async function conEsperaPorLimite<T>(
  pedir: () => Promise<T>, avisar: (esperando: boolean) => void, sigue: () => boolean,
): Promise<T> {
  for (let intento = 1; ; intento++) {
    if (!sigue()) throw CANCELADO;
    try {
      return await pedir();
    } catch (e) {
      if ((e as { status?: number } | null)?.status !== 429) throw e;
      if (intento > REINTENTOS_POR_LIMITE) throw SIGUE_EL_LIMITE;
      avisar(true);
      await new Promise((r) => setTimeout(r, esperaPorLimite(e, intento) * 1000));
      avisar(false);
    }
  }
}
